import { ComponentProps } from "react"
import { Loader2 } from "lucide-react"

import bgDefaultUrl from "@/assets/textures/large-button-bg-default.svg?url"
import bgGreenUrl from "@/assets/textures/large-button-bg-green.svg?url"
import bgGreenPressedUrl from "@/assets/textures/large-button-bg-green-pressed.svg?url"
import bgOrangeUrl from "@/assets/textures/large-button-bg-orange.svg?url"
import bgOrangePressedUrl from "@/assets/textures/large-button-bg-orange-pressed.svg?url"
import { horizontalNineSliceStyle } from "@/shared/lib/horizontalNineSlice"

import { cn } from "@/shared/lib/utils"

/** Native large CTA artboard 358×64. */
const LARGE_SLICE = { left: 75, right: 54 } as const

const largeNineSlice = (url: string) =>
  horizontalNineSliceStyle({
    url,
    sliceLeft: LARGE_SLICE.left,
    sliceRight: LARGE_SLICE.right,
    capWidthLeft: LARGE_SLICE.left,
    capWidthRight: LARGE_SLICE.right,
  })

const LargeButton = ({
  className,
  children,
  variant = "default",
  isLoading,
  ...props
}: ComponentProps<"button"> & {
  variant?: "default" | "green" | "orange"
  isLoading?: boolean
}) => {
  return (
    <button
      className={cn(
        "group relative flex h-16 w-full items-center justify-center disabled:pointer-events-none disabled:opacity-40",
        className
      )}
      {...props}
    >
      {variant === "default" ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={largeNineSlice(bgDefaultUrl)}
        />
      ) : variant === "green" ? (
        <>
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 drop-shadow-[0_0_10px_rgba(0,254,116,0.4)] group-active:opacity-0"
            style={largeNineSlice(bgGreenUrl)}
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-0 group-active:opacity-100"
            style={largeNineSlice(bgGreenPressedUrl)}
          />
        </>
      ) : (
        <>
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 drop-shadow-[0_0_10px_rgba(254,219,78,0.4)] group-active:opacity-0"
            style={largeNineSlice(bgOrangeUrl)}
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-0 group-active:opacity-100"
            style={largeNineSlice(bgOrangePressedUrl)}
          />
        </>
      )}

      {isLoading ? (
        <Loader2 className="relative animate-spin" strokeWidth={3} />
      ) : (
        <span className="relative flex items-center gap-1 text-[22px]/none font-black tracking-[-0.5px] drop-shadow-[0px_1px_0px_rgb(0,0,0)] [text-shadow:_0.5px_0_0.5px_black,_-0.5px_0_0.5px_black,_0px_-0.5px_0.5px_black]">
          {children}
        </span>
      )}
    </button>
  )
}

export default LargeButton
